import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, LogOut, ChevronDown, ChevronRight, Home, MessageCircle, User, Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// =====================================================================
// Desktop: recursive flyout dropdown (supports unlimited nesting)
// =====================================================================
function DesktopMenuItem({ node, depth = 0, onAnyClick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const hasChildren = node.children && node.children.length > 0;

  // Click-outside only matters for top level
  useEffect(() => {
    if (depth !== 0) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [depth]);

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // Leaf item -> simple link
  if (!hasChildren) {
    if (depth === 0) {
      return node.is_external ? (
        <a
          href={node.url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-2 text-amber-900 hover:text-orange-600 hover:bg-amber-50 rounded-md text-sm font-medium transition-colors"
        >
          {node.label}
        </a>
      ) : (
        <Link
          to={node.url || '/'}
          className="px-3 py-2 text-amber-900 hover:text-orange-600 hover:bg-amber-50 rounded-md text-sm font-medium transition-colors"
          data-testid={`nav-${node.id}`}
        >
          {node.label}
        </Link>
      );
    }
    // Nested leaf item inside a dropdown
    return node.is_external ? (
      <a
        href={node.url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onAnyClick}
        className="block px-4 py-2.5 text-amber-800 hover:text-orange-600 hover:bg-amber-50 text-sm transition-colors"
        data-testid={`nav-dropdown-${node.id}`}
      >
        {node.label}
      </a>
    ) : (
      <Link
        to={node.url || '#'}
        onClick={onAnyClick}
        className="block px-4 py-2.5 text-amber-800 hover:text-orange-600 hover:bg-amber-50 text-sm transition-colors"
        data-testid={`nav-dropdown-${node.id}`}
      >
        {node.label}
      </Link>
    );
  }

  // Parent with children
  if (depth === 0) {
    // Top-level dropdown
    return (
      <div
        className="relative"
        ref={ref}
        onMouseEnter={() => { cancelClose(); setOpen(true); }}
        onMouseLeave={scheduleClose}
      >
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 px-3 py-2 text-amber-900 hover:text-orange-600 hover:bg-amber-50 rounded-md text-sm font-medium transition-colors"
          data-testid={`nav-menu-${node.id}`}
        >
          {node.label}
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-amber-100 py-1 z-50">
            {node.children.map(child => (
              <DesktopMenuItem
                key={child.id}
                node={child}
                depth={1}
                onAnyClick={() => setOpen(false)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Nested dropdown (depth >= 1): side flyout
  return (
    <div
      className="relative group/sub"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-amber-800 hover:text-orange-600 hover:bg-amber-50 text-sm transition-colors"
        data-testid={`nav-submenu-${node.id}`}
      >
        <span>{node.label}</span>
        <ChevronRight className="w-4 h-4 text-amber-500" />
      </button>
      {open && (
        <div className="absolute top-0 left-full ml-0 w-56 bg-white rounded-xl shadow-xl border border-amber-100 py-1 z-50">
          {node.children.map(child => (
            <DesktopMenuItem
              key={child.id}
              node={child}
              depth={depth + 1}
              onAnyClick={onAnyClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Mobile: recursive accordion (supports unlimited nesting)
// =====================================================================
function MobileMenuItem({ node, depth = 0, onClose }) {
  const [open, setOpen] = useState(false);
  const hasChildren = node.children && node.children.length > 0;

  // Indentation increases with depth
  const indentPx = depth * 12;

  if (!hasChildren) {
    if (node.is_external) {
      return (
        <a
          href={node.url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          style={{ paddingLeft: 16 + indentPx }}
          className="block pr-4 py-3 text-amber-900 hover:bg-amber-100/60 rounded-xl text-[15px] font-medium active:scale-[0.98] transition-all"
        >
          {node.label}
        </a>
      );
    }
    return (
      <Link
        to={node.url || '/'}
        onClick={onClose}
        style={{ paddingLeft: 16 + indentPx }}
        className="block pr-4 py-3 text-amber-900 hover:bg-amber-100/60 rounded-xl text-[15px] font-medium active:scale-[0.98] transition-all"
        data-testid={`mobile-nav-${node.id}`}
      >
        {node.label}
      </Link>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{ paddingLeft: 16 + indentPx }}
        className="w-full flex items-center justify-between pr-4 py-3 text-amber-900 hover:bg-amber-100/60 rounded-xl text-[15px] font-medium active:scale-[0.98] transition-all"
        data-testid={`mobile-nav-toggle-${node.id}`}
      >
        <span>{node.label}</span>
        <ChevronDown className={`w-4 h-4 text-amber-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-0.5 mb-0.5 space-y-0.5 animate-in slide-in-from-top-2 duration-150">
          {node.children.map(child => (
            <MobileMenuItem
              key={child.id}
              node={child}
              depth={depth + 1}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Helper: build tree from flat list with parent_id
// =====================================================================
function buildMenuTree(flatMenus) {
  const byId = {};
  const sorted = [...flatMenus].sort((a, b) => (a.order || 0) - (b.order || 0));
  sorted.forEach(m => {
    byId[m.id] = { ...m, children: [] };
  });
  const roots = [];
  sorted.forEach(m => {
    if (m.parent_id && byId[m.parent_id]) {
      byId[m.parent_id].children.push(byId[m.id]);
    } else {
      roots.push(byId[m.id]);
    }
  });
  return roots;
}

// =====================================================================
// Main Navbar component
// =====================================================================
export default function Navbar({ user, setUser }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navMenus, setNavMenus] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/nav-menus`)
      .then(r => r.json())
      .then(data => setNavMenus(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('chatSince');
    setUser(null);
    toast.success('Çıkış yapıldı');
    navigate('/');
  };

  const menuTree = buildMenuTree(navMenus);

  return (
    <>
      <nav className="bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-amber-100/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group" data-testid="navbar-logo">
              <img
                src="/logo-128.png"
                alt="Kızılören Kasabası logosu"
                className="w-10 h-10 rounded-xl object-cover shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all"
              />
              <div className="flex flex-col leading-tight">
                <span className="text-[15px] sm:text-lg font-bold text-amber-900 tracking-tight" style={{ fontFamily: 'Merriweather, serif' }}>
                  Kızılören
                </span>
                <span className="text-[10px] sm:text-xs text-amber-600 font-medium tracking-wide -mt-0.5">KASABASI</span>
              </div>
            </Link>

            {/* Desktop menu */}
            <div className="hidden lg:flex items-center gap-0.5">
              <Link to="/" className="px-3 py-2 text-amber-900 hover:text-orange-600 hover:bg-amber-50 rounded-md text-sm font-medium transition-colors" data-testid="nav-home">
                Anasayfa
              </Link>

              {menuTree.map(node => (
                <DesktopMenuItem key={node.id} node={node} depth={0} />
              ))}

              {user ? (
                <>
                  <Link to="/chat" className="px-3 py-2 text-amber-900 hover:text-orange-600 hover:bg-amber-50 rounded-md text-sm font-medium transition-colors">
                    Sohbet
                  </Link>
                  <Link to="/profil" className="px-3 py-2 text-amber-900 hover:text-orange-600 hover:bg-amber-50 rounded-md text-sm font-medium transition-colors">
                    Profilim
                  </Link>
                  {(user.role === 'admin' || user.role === 'editor') && (
                    <Link to="/admin" className="px-3 py-2 text-amber-900 hover:text-orange-600 hover:bg-amber-50 rounded-md text-sm font-medium transition-colors">
                      Yönetim
                    </Link>
                  )}
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    size="sm"
                    className="ml-1 text-amber-900 border-amber-300 hover:bg-amber-50"
                    data-testid="nav-logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Çıkış
                  </Button>
                </>
              ) : (
                <Link to="/uyelik" className="ml-1">
                  <Button size="sm" className="bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700 shadow-sm" data-testid="nav-login-btn">
                    Üye Girişi
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden w-10 h-10 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 flex items-center justify-center active:scale-95 transition-all"
              data-testid="mobile-menu-toggle"
              aria-label="Menüyü aç"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu - slide-in drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        aria-hidden={!mobileMenuOpen}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Drawer panel */}
        <aside
          className={`absolute right-0 top-0 h-full w-[85%] max-w-sm bg-gradient-to-b from-white to-amber-50/50 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
          data-testid="mobile-menu-drawer"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-amber-100">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2.5">
              <img
                src="/logo-128.png"
                alt="Kızılören Kasabası logosu"
                className="w-10 h-10 rounded-xl object-cover shadow-sm"
              />
              <div className="flex flex-col leading-tight">
                <span className="text-base font-bold text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>Kızılören</span>
                <span className="text-[10px] text-amber-600 font-medium tracking-wide -mt-0.5">KASABASI</span>
              </div>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-9 h-9 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-900 flex items-center justify-center active:scale-95 transition-all"
              aria-label="Menüyü kapat"
              data-testid="mobile-menu-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* User card */}
          {user && (
            <div className="mx-5 mt-4 p-3.5 rounded-2xl bg-gradient-to-br from-amber-600 to-orange-600 text-white shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-lg font-bold">
                  {user.nickname?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{user.nickname}</p>
                  <p className="text-amber-100 text-xs truncate">
                    {user.role === 'admin' ? 'Yönetici' : user.role === 'editor' ? 'Editör' : 'Üye'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Scrollable nav links */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5 overscroll-contain">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-amber-900 hover:bg-amber-100/60 rounded-xl text-[15px] font-medium active:scale-[0.98] transition-all"
            >
              <Home className="w-4 h-4 text-amber-600" />
              Anasayfa
            </Link>

            {menuTree.map(node => (
              <MobileMenuItem
                key={node.id}
                node={node}
                depth={0}
                onClose={() => setMobileMenuOpen(false)}
              />
            ))}

            {user && (
              <>
                <div className="my-3 border-t border-amber-100 mx-2" />
                <Link
                  to="/chat"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-amber-900 hover:bg-amber-100/60 rounded-xl text-[15px] font-medium active:scale-[0.98] transition-all"
                >
                  <MessageCircle className="w-4 h-4 text-amber-600" />
                  Sohbet
                </Link>
                <Link
                  to="/profil"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-amber-900 hover:bg-amber-100/60 rounded-xl text-[15px] font-medium active:scale-[0.98] transition-all"
                >
                  <User className="w-4 h-4 text-amber-600" />
                  Profilim
                </Link>
                {(user.role === 'admin' || user.role === 'editor') && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-amber-900 hover:bg-amber-100/60 rounded-xl text-[15px] font-medium active:scale-[0.98] transition-all"
                  >
                    <Shield className="w-4 h-4 text-amber-600" />
                    Yönetim
                  </Link>
                )}
              </>
            )}
          </div>

          {/* Footer CTA */}
          <div className="border-t border-amber-100 p-4">
            {user ? (
              <Button
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full h-11 text-amber-900 border-amber-300 hover:bg-amber-50 rounded-xl"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış Yap
              </Button>
            ) : (
              <Link to="/uyelik" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full h-11 bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700 rounded-xl shadow-sm">
                  Üye Girişi
                </Button>
              </Link>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}
